import { useState, useCallback, useMemo, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import {
    Button,
    Box,
    Flex,
    FormLabel,
    Divider,
    Heading,
    HStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Radio,
    Text,
    SimpleGrid,
    useMediaQuery,
    useDisclosure,
} from '@chakra-ui/react';
import { Formik, Form, FieldArray, setNestedObjectValues } from 'formik';
import { set, isBefore, isAfter, isEqual } from 'date-fns';

import {
    createSubBattery,
    createSubBatteryFromWizard,
    validateSubBattery,
    validateSubBatteryFromWizard,
} from '../../services/batteries';
import { isSubmitDisabled, removeNullish } from '../../services/utils';
import { isNumberCellEmpty } from '../../services/items';
import { mapGeneralValidationMessages } from '../autoform/utils/autoformUtils';
import { getAutoformLevelDropdowns } from '../../store/autoform/autoformApi';

import useCommonToast from '../../hooks/useCommonToast';

import GeneralFormValidationsAlert from '../forms/GeneralFormValidationsAlert';

import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';
import DateFormikField from '../forms/DateFormikField';
import NumberInputFormikField from '../forms/NumberInputFormikField';
import RadioGroupFormikField from '../forms/RadioGroupFormikField';
import NumberInputField from '../forms/NumberInputField';
import HelpButtonComponent from 'components/utils/HelpButtonComponent';
import { helpAnchors } from 'constants/help';

import styled from '@emotion/styled/macro';
import AddItemButton from 'components/utils/AddItemButton';
import mappedItemTypes from 'services/items/mappedItemTypes';
import { TEN_SECOND_DURATION_IN_MILLISECONDS } from 'constants/time';
import { ReactComponent as AddIcon } from 'icons/add.svg';
import { ReactComponent as DeleteIcon } from 'icons/delete.svg';

const AddSubBatteryModal = ({ headerItem, batteryTypes, batteryItemId, updateBatteries, ...rest }) => {
    const intl = useIntl();
    const { toast } = useCommonToast();
    const dispatch = useDispatch();
    const [isDesktop] = useMediaQuery('(min-width: 769px)');
    const errorsDisclosure = useDisclosure({ defaultIsOpen: true });
    const warningsDisclosure = useDisclosure({ defaultIsOpen: true });

    const [wizardMode, setWizardMode] = useState(true); // 'wizard' holds the complete setup, 'basic' holds two only fields
    const [isValidating, setIsValidating] = useState(false);
    const [generalErrors, setGeneralErrors] = useState([]);
    const [generalWarnings, setGeneralWarnings] = useState([]);
    const [globalErrors, setGlobalErrors] = useState([]);
    const [globalWarnings, setGlobalWarnings] = useState([]);
    const [singleDatePeriodReadOnly, setSingleDatePeriodReadOnly] = useState(false);
    const [annualDegradation, setAnnualDegradation] = useState(0);
    const [ancillaryDropdowns, setAncillaryDropdowns] = useState({
        peakPeriods: [],
        ancillaryProducts: [],
    });
    const [ancillaryDataLoaded, setAncillaryDataLoaded] = useState(false);
    const [displayAncillaries, setDisplayAncillaries] = useState(false);

    const allErrors = useMemo(() => [...globalErrors, ...generalErrors], [generalErrors, globalErrors]);
    const allWarnings = useMemo(() => [...globalWarnings, ...generalWarnings], [generalWarnings, globalWarnings]);
    const metadata = useSelector((state) => state.autoform.metadata);

    const formLabels = {
        description: intl.formatMessage({ id: 'batteries_sub_battery_name_label' }),
        batteryType: intl.formatMessage({ id: 'batteries_battery_type' }),
        startDate: intl.formatMessage({ id: 'common_grid_start_date' }),
        endDate: intl.formatMessage({ id: 'common_grid_end_date' }),
        mwPower: intl.formatMessage({ id: 'batteries_battery_wizard_mw_power' }),
        hrDuration: intl.formatMessage({ id: 'batteries_battery_wizard_hr_duration' }),
        chargeEfficiency: intl.formatMessage({ id: 'batteries_battery_wizard_charge_efficiency' }),
        dischargeEfficiency: intl.formatMessage({ id: 'batteries_battery_wizard_discharge_efficiency' }),
        vom: intl.formatMessage({ id: 'batteries_battery_wizard_vom_discharging' }),
        fixedCost: intl.formatMessage({ id: 'batteries_battery_wizard_fixed_costs' }),
        annualCycleLimit: intl.formatMessage({ id: 'batteries_battery_wizard_annual_cycle_limit' }),
        costlessAdder: intl.formatMessage({ id: 'batteries_battery_wizard_costless_adder' }),
        minAverageSOC: intl.formatMessage({ id: 'batteries_battery_wizard_min_average_soc' }),
        maxAverageSOC: intl.formatMessage({ id: 'batteries_battery_wizard_max_average_soc' }),
        annualDegradation: intl.formatMessage({ id: 'batteries_battery_wizard_annual_degradation' }),
        peakPeriod: intl.formatMessage({ id: 'batteries_battery_wizard_peak_period' }),
        ancillaryProduct: intl.formatMessage({ id: 'batteries_battery_wizard_ancillary_product' }),
        allowedContribution: intl.formatMessage({ id: 'batteries_battery_wizard_allowed_contribution' }),
        emptyPlaceholder: '',
    };

    const initialFormValues = {
        description: '',
        batteryTypeId: '',
        itemId: parseInt(batteryItemId), // the parent battery item id
        startDate: '',
        endDate: '',
        mwPower: '',
        hrDuration: '',
        chargeEfficiency: '',
        dischargeEfficiency: '',
        vom: '',
        fixedCost: '',
        annualCycleLimit: '',
        costlessAdder: '',
        minAverageSOC: 0,
        maxAverageSOC: 100,
        annualDegradation: '',
        periodMode: '0',
        ancillaryContributions: [],
    };

    // Fetch ancillary contributions metadata and dropdown data once on mount
    useEffect(() => {
        const fetchAncillaryData = async () => {
            try {
                // Check if metadata is loaded
                if (!metadata) {
                    return;
                }

                // Find the ancillary contributions table
                const ancillaryTable = metadata.tables?.find(
                    (table) => table.name.toLowerCase() === 'ancillary contributions'
                );

                if (!ancillaryTable) {
                    setDisplayAncillaries(false);
                } else {
                    // Find the column IDs by name
                    const peakPeriodColumn = ancillaryTable.columns?.find(
                        (col) => col.name.toLowerCase() === 'peakperiodlabelid'
                    );
                    const ancillaryProductColumn = ancillaryTable.columns?.find(
                        (col) => col.name.toLowerCase() === 'ancillaryproductid'
                    );

                    if (!peakPeriodColumn || !ancillaryProductColumn) {
                        setDisplayAncillaries(false);
                    } else {
                        // Fetch dropdown data using Redux action
                        const action = await dispatch(
                            getAutoformLevelDropdowns({
                                tableId: ancillaryTable.id,
                                parameters: [mappedItemTypes.BATTERY_STORAGE.id, batteryItemId],
                            })
                        );

                        if (action.meta?.requestStatus === 'fulfilled') {
                            const dropdownData = action.payload.data;

                            // Extract dropdown options using the dynamic column IDs
                            const peakPeriodData = dropdownData.find((col) => col.columnId === peakPeriodColumn.id);
                            const ancillaryProductData = dropdownData.find(
                                (col) => col.columnId === ancillaryProductColumn.id
                            );

                            setAncillaryDropdowns({
                                peakPeriods: peakPeriodData?.lookupData || [],
                                ancillaryProducts: ancillaryProductData?.lookupData || [],
                            });

                            setAncillaryDataLoaded(true);
                            setDisplayAncillaries(true);
                        }
                    }
                }
            } catch (error) {
                toast({
                    title: intl.formatMessage({ id: 'common_error' }),
                    description: 'Failed to load ancillary contributions data',
                    status: 'error',
                    duration: TEN_SECOND_DURATION_IN_MILLISECONDS,
                });
            }
        };

        if (batteryItemId && metadata && !ancillaryDataLoaded) {
            fetchAncillaryData();
        }
    }, [batteryItemId, metadata, ancillaryDataLoaded, toast, intl, dispatch]);

    const validateDescription = (value) => {
        const trimmed = value.trim();

        if (!trimmed) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label: formLabels.description });
        }

        if (trimmed.length > 50) {
            return intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: formLabels.description, lengthRule: '50 characters or less' }
            );
        }
    };

    const validateRequiredField = (value, label) => {
        if (value === '') {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label });
        }
    };

    const validateSiblingDate = ({ value, siblingValue, type }) => {
        // when you delete the contents of the datepicker the value is null or empty string
        const isInvalidValue = value === null || value === '';
        const isInvalidSiblingValue = siblingValue === null || siblingValue === '';

        if (isInvalidValue || (isInvalidValue && isInvalidSiblingValue)) {
            // show an error if the value is invalid or both the value and the sibling value are invalid
            return intl.formatMessage(
                { id: 'common_forms_validation_required' },
                { label: type === 'start' ? formLabels.startDate : formLabels.endDate }
            );
        }

        // skip validation on invalid sibling
        if (isInvalidSiblingValue) {
            return null;
        }

        const dateA = set(value, { seconds: 0, milliseconds: 0 });
        const dateB = set(siblingValue, { seconds: 0, milliseconds: 0 });

        if (type === 'start' && (isAfter(dateA, dateB) || isEqual(dateA, dateB))) {
            return intl.formatMessage({ id: 'batteries_common_modals_date_smaller' });
        } else if (type === 'end' && (isBefore(dateA, dateB) || isEqual(dateA, dateB))) {
            return intl.formatMessage({ id: 'batteries_common_modals_date_bigger' });
        }

        return null;
    };

    const validateForm = (values) => {
        const description = validateDescription(values.description);

        if (!wizardMode) {
            return {
                ...(description ? { description } : null),
            };
        }

        const startDate = validateSiblingDate({
            value: values.startDate,
            siblingValue: values.endDate,
            type: 'start',
        });

        const endDate = validateSiblingDate({
            value: values.endDate,
            siblingValue: values.startDate,
            type: 'end',
        });

        const mwPower = validateRequiredField(values.mwPower, formLabels.mwPower);
        const hrDuration = validateRequiredField(values.hrDuration, formLabels.hrDuration);
        const chargeEfficiency = validateRequiredField(values.chargeEfficiency, formLabels.chargeEfficiency);
        const dischargeEfficiency = validateRequiredField(values.dischargeEfficiency, formLabels.dischargeEfficiency);

        // Validate ancillary contributions
        const ancillaryContributions = {};
        values.ancillaryContributions?.forEach((contribution, index) => {
            const errors = {};

            if (!contribution.peakPeriodId) {
                errors.peakPeriodId = intl.formatMessage(
                    { id: 'common_forms_validation_required' },
                    { label: formLabels.peakPeriod }
                );
            }

            if (!contribution.ancillaryProductId) {
                errors.ancillaryProductId = intl.formatMessage(
                    { id: 'common_forms_validation_required' },
                    { label: formLabels.ancillaryProduct }
                );
            }

            if (contribution.allowedContribution === '' || contribution.allowedContribution === null) {
                errors.allowedContribution = intl.formatMessage(
                    { id: 'common_forms_validation_required' },
                    { label: formLabels.allowedContribution }
                );
            }

            if (Object.keys(errors).length > 0) {
                ancillaryContributions[index] = errors;
            }
        });

        return removeNullish({
            description,
            startDate,
            endDate,
            mwPower,
            hrDuration,
            chargeEfficiency,
            dischargeEfficiency,
            ...(Object.keys(ancillaryContributions).length > 0 && { ancillaryContributions }),
        });
    };

    const formatFieldValues = useCallback(
        (values) => {
            return {
                itemId: values.itemId,
                description: values.description,
                startDate: values.startDate || null,
                endDate: values.endDate || null,
                mwPower: isNumberCellEmpty(values.mwPower),
                hrDuration: isNumberCellEmpty(values.hrDuration),
                chargeEfficiency: isNumberCellEmpty(values.chargeEfficiency),
                dischargeEfficiency: isNumberCellEmpty(values.dischargeEfficiency),
                vom: isNumberCellEmpty(values.vom),
                fixedCost: isNumberCellEmpty(values.fixedCost),
                annualCycleLimit: isNumberCellEmpty(values.annualCycleLimit),
                costlessAdder: isNumberCellEmpty(values.costlessAdder),
                minAverageSOC: isNumberCellEmpty(values.minAverageSOC),
                maxAverageSOC: isNumberCellEmpty(values.maxAverageSOC),
                annualDegradation: isNumberCellEmpty(annualDegradation),
                periodMode: isNumberCellEmpty(values.periodMode),
                ancillaryContributions: values.ancillaryContributions.map((contribution) => ({
                    peakPeriodId: contribution.peakPeriodId || null,
                    ancillaryProductId: contribution.ancillaryProductId || null,
                    allowedContribution: isNumberCellEmpty(contribution.allowedContribution),
                })),
            };
        },
        [annualDegradation]
    );

    const resetServerValidation = useCallback(() => {
        setGeneralErrors([]);
        setGeneralWarnings([]);
        setGlobalErrors([]);
        setGlobalWarnings([]);
    }, []);

    const onFormSubmit = async (values, { setErrors, setStatus }) => {
        try {
            const successMessage = intl.formatMessage({ id: 'common_item_creation_success' }, { type: headerItem });

            const payload = formatFieldValues(values);

            const { data } = wizardMode
                ? await createSubBatteryFromWizard(payload)
                : await createSubBattery({
                      description: values.description,
                      batteryTypeId: values.batteryTypeId || null,
                      itemId: values.itemId,
                  });

            updateBatteries(data.subBatteryResponse, payload.itemId);
            rest.onClose();
            toast(successMessage);
        } catch (err) {
            // reset the previous general errors and warnings
            resetServerValidation();

            // make sure to re-open the disclosures again,
            // because the user might have closed them and is submitting the form again
            errorsDisclosure.onOpen();
            warningsDisclosure.onOpen();

            if (err.response.status !== 500) {
                if (err.response.data.validationResult.issues) {
                    // when creating a single record we expect at most only 1 issue
                    const issue = err.response.data.validationResult.issues[0];

                    // handle general errors and warnings
                    const { warnings, errors } = mapGeneralValidationMessages(issue.generalValidationMessages);
                    setGeneralWarnings(warnings);
                    setGeneralErrors(errors);

                    // handle specific field errors
                    let fieldErrors = {};
                    let fieldStatus = {};

                    for (const field of issue.fields) {
                        const errors = field.validationMessages
                            .filter(({ severity }) => severity === 'Error')
                            .map(({ message }) => message);

                        const warnings = field.validationMessages
                            .filter(({ severity }) => severity === 'Warning')
                            .map(({ message }) => message);
                        fieldStatus[field.name] = { errors, warnings };
                    }

                    setErrors(fieldErrors);
                    setStatus(fieldStatus);
                }

                if (err.response.data.validationResult.globalIssues) {
                    // handle global errors and warnings
                    const { warnings, errors } = mapGeneralValidationMessages(
                        err.response.data.validationResult.globalIssues
                    );
                    setGlobalWarnings(warnings);
                    setGlobalErrors(errors);
                }
            }
        }
    };

    const onServerValidate = useCallback(
        async (values, setErrors, setStatus, setTouched, validateForm) => {
            const errors = await validateForm(values);

            // reset the previous general errors and warnings
            resetServerValidation();

            if (Object.keys(errors).length > 0) {
                setTouched(setNestedObjectValues(errors, true));
            } else {
                try {
                    setIsValidating(true);

                    const payload = formatFieldValues(values);

                    const data = wizardMode ? await validateSubBatteryFromWizard(payload) : validateSubBattery(payload);

                    // make sure to re-open the disclosure again
                    warningsDisclosure.onOpen();

                    if (data.issues) {
                        // reset the previous general warnings
                        setGeneralWarnings([]);

                        // when creating a single record we expect at most only 1 issue
                        const issue = data.issues[0];

                        // handle general warnings
                        const { warnings } = mapGeneralValidationMessages(issue.generalValidationMessages);
                        setGeneralWarnings(warnings);

                        // handle specific field warnings
                        let fieldStatus = {};

                        for (const field of issue.fields) {
                            const warnings = field.validationMessages
                                .filter(({ severity }) => severity === 'Warning')
                                .map(({ message }) => message);

                            fieldStatus[field.name] = { warnings };
                        }

                        setStatus(fieldStatus);
                        setTouched(setNestedObjectValues(fieldStatus, true));
                    }

                    if (data.globalIssues) {
                        // handle global warnings
                        const { warnings } = mapGeneralValidationMessages(data.globalIssues);
                        setGlobalWarnings(warnings);
                    }

                    if (!data) {
                        toast(intl.formatMessage({ id: 'common_check_data_success' }));
                    }
                } catch (err) {
                    // make sure to re-open the disclosures again,
                    // because the user might have closed them and is submitting the form again
                    errorsDisclosure.onOpen();
                    warningsDisclosure.onOpen();

                    if (err.response?.status !== 500) {
                        if (err.response?.data?.issues) {
                            // when creating a single record we expect at most only 1 issue
                            const issue = err.response.data.issues[0];

                            // handle general errors and warnings
                            const { warnings, errors } = mapGeneralValidationMessages(issue.generalValidationMessages);
                            setGeneralWarnings(warnings);
                            setGeneralErrors(errors);

                            // handle specific field errors
                            let fieldErrors = {};
                            let fieldStatus = {};

                            for (const field of issue.fields) {
                                const errors = field.validationMessages
                                    .filter(({ severity }) => severity === 'Error')
                                    .map(({ message }) => message);

                                const warnings = field.validationMessages
                                    .filter(({ severity }) => severity === 'Warning')
                                    .map(({ message }) => message);
                                fieldStatus[field.name] = { errors, warnings };
                            }

                            setErrors(fieldErrors);
                            setStatus(fieldStatus);
                            setTouched(setNestedObjectValues(fieldStatus, true));
                        }

                        if (err.response?.data?.globalIssues) {
                            // handle global errors and warnings
                            const { warnings, errors } = mapGeneralValidationMessages(err.response.data.globalIssues);
                            setGlobalWarnings(warnings);
                            setGlobalErrors(errors);
                        }
                    }
                } finally {
                    setIsValidating(false);
                }
            }
        },
        [intl, toast, errorsDisclosure, warningsDisclosure, wizardMode, formatFieldValues, resetServerValidation]
    );

    const degradationCheckDateMode = useCallback((degradationValue, setFieldValue, currentPeriodMode) => {
        setAnnualDegradation(degradationValue);

        if (degradationValue === '' || degradationValue === null || degradationValue <= 0) {
            //no degradation value set, so allow single date period
            setSingleDatePeriodReadOnly(false);
        } else if (degradationValue !== '' && degradationValue !== null && degradationValue > 0) {
            //degradation value set, so disable single date period
            setSingleDatePeriodReadOnly(true);
            // Only change to annual ('0') if currently set to single date period ('2')
            if (currentPeriodMode === '2') {
                setFieldValue('periodMode', '0');
            }
        }
    }, []);

    return (
        <Modal {...rest}>
            <ModalOverlay />

            <ModalContent pt={3} borderRadius={isDesktop ? '2xl' : 0}>
                <ModalHeader>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        {wizardMode ? (
                            <FormattedMessage id="batteries_battery_wizard_title" />
                        ) : (
                            <FormattedMessage
                                id="common_add_subitem_modal_heading"
                                values={{ item: formLabels.description }}
                            />
                        )}
                        <HelpButtonComponent helpAnchor={helpAnchors.BATTERY_NEW_SETUP} />
                    </Heading>
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                <ModalBody borderTop="1px" borderColor="gray.200">
                    {wizardMode && (
                        <Box pt={3} pb={6}>
                            <FormattedMessage id="batteries_battery_wizard_explanation" />
                        </Box>
                    )}

                    <Formik
                        enableReinitialize
                        initialValues={initialFormValues}
                        onSubmit={onFormSubmit}
                        validate={validateForm}
                    >
                        {({
                            values,
                            isSubmitting,
                            errors,
                            touched,
                            setErrors,
                            setStatus,
                            setTouched,
                            setFieldValue,
                        }) => {
                            const isDisabled = isSubmitDisabled({ errors, touched });

                            return (
                                <Form noValidate>
                                    {allErrors.length > 0 && (
                                        <GeneralFormValidationsAlert
                                            isOpen={errorsDisclosure.isOpen}
                                            onClose={errorsDisclosure.onClose}
                                            messages={allErrors}
                                        />
                                    )}

                                    {allWarnings.length > 0 && (
                                        <GeneralFormValidationsAlert
                                            status="warning"
                                            title={<FormattedMessage id="common_warning" />}
                                            isOpen={warningsDisclosure.isOpen}
                                            onClose={warningsDisclosure.onClose}
                                            messages={allWarnings}
                                        />
                                    )}

                                    <SimpleGrid columns={1} spacingY={3} pt={!wizardMode && 3} pb={3}>
                                        <InputFormikField
                                            name="description"
                                            type="text"
                                            label={formLabels.description}
                                            isRequired
                                        />

                                        {!wizardMode && (
                                            <ComboboxFormikField
                                                id="batteryTypeId"
                                                name="batteryTypeId"
                                                label={formLabels.batteryType}
                                                options={batteryTypes}
                                                valueKey="id"
                                                labelKey="description"
                                                showPlaceholder={true}
                                            />
                                        )}
                                    </SimpleGrid>

                                    {wizardMode && (
                                        <>
                                            <Flex>
                                                <FormattedMessage id="batteries_battery_wizard_date_mode" />
                                            </Flex>

                                            <RadioGroupFormikField mx={0} mb={3} id="dateMode" name="periodMode">
                                                <HStack spacing={8}>
                                                    <StyledRadio value={'0'}>{'Annual'}</StyledRadio>
                                                    <StyledRadio value={'1'}>{'Monthly'}</StyledRadio>
                                                    {!singleDatePeriodReadOnly && (
                                                        <StyledRadio value={'2'} disabled={singleDatePeriodReadOnly}>
                                                            {'Single date period'}
                                                        </StyledRadio>
                                                    )}
                                                </HStack>
                                            </RadioGroupFormikField>

                                            <SimpleGrid columns={2} gap={6} py={3}>
                                                <DateFormikField
                                                    id="startDate"
                                                    name="startDate"
                                                    label={formLabels.startDate}
                                                    isRequired
                                                    showTimeInput={false}
                                                />

                                                <DateFormikField
                                                    id="endDate"
                                                    name="endDate"
                                                    label={formLabels.endDate}
                                                    isRequired
                                                    showTimeInput={false}
                                                />

                                                <NumberInputFormikField
                                                    id="mwPower"
                                                    name="mwPower"
                                                    label={formLabels.mwPower}
                                                    isRequired
                                                />

                                                <NumberInputFormikField
                                                    id="hrDuration"
                                                    name="hrDuration"
                                                    label={formLabels.hrDuration}
                                                    isRequired
                                                />

                                                <NumberInputFormikField
                                                    id="chargeEfficiency"
                                                    name="chargeEfficiency"
                                                    label={formLabels.chargeEfficiency}
                                                    isRequired
                                                    clampValueOnBlur={true}
                                                    keepWithinRange={true}
                                                    min={0}
                                                    max={100}
                                                />

                                                <NumberInputFormikField
                                                    id="dischargeEfficiency"
                                                    name="dischargeEfficiency"
                                                    label={formLabels.dischargeEfficiency}
                                                    isRequired
                                                    clampValueOnBlur={true}
                                                    keepWithinRange={true}
                                                    min={0}
                                                    max={100}
                                                />
                                            </SimpleGrid>

                                            <Divider my={6} />

                                            <Heading as="h4" variant="h4" textTransform="capitalize">
                                                <FormattedMessage
                                                    id="batteries_battery_wizard_optional_title"
                                                    textTransform="capitalize"
                                                />
                                            </Heading>
                                            <Box pb={3}>
                                                <FormattedMessage
                                                    id="batteries_battery_wizard_optional_explanation"
                                                    textTransform="capitalize"
                                                />
                                            </Box>

                                            <SimpleGrid columns={2} gap={6} py={3}>
                                                <NumberInputFormikField
                                                    id="vom"
                                                    name="vom"
                                                    label={formLabels.vom}
                                                    isRequired={false}
                                                />

                                                <NumberInputFormikField
                                                    id="fixedCost"
                                                    name="fixedCost"
                                                    label={formLabels.fixedCost}
                                                    isRequired={false}
                                                />

                                                <NumberInputFormikField
                                                    id="annualCycleLimit"
                                                    name="annualCycleLimit"
                                                    label={formLabels.annualCycleLimit}
                                                    isRequired={false}
                                                />

                                                <NumberInputFormikField
                                                    id="costlessAdder"
                                                    name="costlessAdder"
                                                    label={formLabels.costlessAdder}
                                                    isRequired={false}
                                                />

                                                <NumberInputFormikField
                                                    id="minAverageSOC"
                                                    name="minAverageSOC"
                                                    label={formLabels.minAverageSOC}
                                                    isRequired={false}
                                                    clampValueOnBlur={true}
                                                    keepWithinRange={true}
                                                    min={0}
                                                    max={100}
                                                />

                                                <NumberInputFormikField
                                                    id="maxAverageSOC"
                                                    name="maxAverageSOC"
                                                    label={formLabels.maxAverageSOC}
                                                    isRequired={false}
                                                    clampValueOnBlur={true}
                                                    keepWithinRange={true}
                                                    min={0}
                                                    max={100}
                                                />
                                                <NumberInputField
                                                    id="annualDegradation"
                                                    name="annualDegradation"
                                                    label={formLabels.annualDegradation}
                                                    placeholder="-"
                                                    min={0}
                                                    max={100}
                                                    value={annualDegradation}
                                                    onChange={(value) =>
                                                        degradationCheckDateMode(
                                                            value,
                                                            setFieldValue,
                                                            values.periodMode
                                                        )
                                                    }
                                                    info={
                                                        <FormattedMessage id="batteries_battery_wizard_annual_degradation_info" />
                                                    }
                                                />
                                            </SimpleGrid>
                                            {displayAncillaries && (
                                                <>
                                                    <Heading
                                                        as="h4"
                                                        variant="h4"
                                                        textTransform="capitalize"
                                                        mb={3}
                                                        mt={6}
                                                    >
                                                        <FormattedMessage id="batteries_battery_wizard_ancillary_contributions_title" />
                                                    </Heading>

                                                    <FieldArray name="ancillaryContributions">
                                                        {({ push, remove }) => (
                                                            <>
                                                                {values.ancillaryContributions.map((row, index) => (
                                                                    <Box key={index} mb={4}>
                                                                        <SimpleGrid
                                                                            columns={4}
                                                                            gap={6}
                                                                            alignItems="start"
                                                                        >
                                                                            <ComboboxFormikField
                                                                                id={`ancillaryContributions.${index}.peakPeriodId`}
                                                                                name={`ancillaryContributions.${index}.peakPeriodId`}
                                                                                label={
                                                                                    index === 0
                                                                                        ? formLabels.peakPeriod
                                                                                        : ''
                                                                                }
                                                                                options={ancillaryDropdowns.peakPeriods}
                                                                                valueKey="value"
                                                                                labelKey="text"
                                                                                showPlaceholder={true}
                                                                                placeholderValue=""
                                                                                placeholderLabel="-"
                                                                                isRequired
                                                                            />

                                                                            <ComboboxFormikField
                                                                                id={`ancillaryContributions.${index}.ancillaryProductId`}
                                                                                name={`ancillaryContributions.${index}.ancillaryProductId`}
                                                                                label={
                                                                                    index === 0
                                                                                        ? formLabels.ancillaryProduct
                                                                                        : ''
                                                                                }
                                                                                options={
                                                                                    ancillaryDropdowns.ancillaryProducts
                                                                                }
                                                                                valueKey="value"
                                                                                labelKey="text"
                                                                                showPlaceholder={true}
                                                                                placeholderValue=""
                                                                                placeholderLabel="-"
                                                                                isRequired
                                                                            />

                                                                            <NumberInputFormikField
                                                                                id={`ancillaryContributions.${index}.allowedContribution`}
                                                                                name={`ancillaryContributions.${index}.allowedContribution`}
                                                                                label={
                                                                                    index === 0
                                                                                        ? formLabels.allowedContribution
                                                                                        : ''
                                                                                }
                                                                                clampValueOnBlur={true}
                                                                                isRequired
                                                                            />

                                                                            <Box>
                                                                                {index === 0 && (
                                                                                    <FormLabel textTransform="capitalize">
                                                                                        <FormattedMessage id="common_delete_row" />
                                                                                    </FormLabel>
                                                                                )}
                                                                                <Button
                                                                                    size="sm"
                                                                                    colorScheme="red"
                                                                                    variant="special"
                                                                                    textTransform="capitalize"
                                                                                    leftIcon={<DeleteIcon />}
                                                                                    onClick={() => remove(index)}
                                                                                    w={{ base: '100%', md: 'auto' }}
                                                                                >
                                                                                    <FormattedMessage id="common_delete" />
                                                                                </Button>
                                                                            </Box>
                                                                        </SimpleGrid>
                                                                    </Box>
                                                                ))}
                                                                <AddItemButton
                                                                    pl="0px !important"
                                                                    m="0px !important"
                                                                    isDisabled={
                                                                        values.ancillaryContributions.length >=
                                                                        ancillaryDropdowns.ancillaryProducts.length
                                                                    }
                                                                    mt={2}
                                                                    leftIcon={<AddIcon />}
                                                                    onClick={() =>
                                                                        push({
                                                                            peakPeriodId: '',
                                                                            ancillaryProductId: '',
                                                                            allowedContribution: '',
                                                                        })
                                                                    }
                                                                >
                                                                    <Text size="sm" color="gray.900" pl={1} pt={1}>
                                                                        <FormattedMessage id="batteries_battery_wizard_add_ancillary_contribution" />
                                                                    </Text>
                                                                </AddItemButton>
                                                            </>
                                                        )}
                                                    </FieldArray>
                                                </>
                                            )}
                                        </>
                                    )}
                                    <ModalFooter
                                        justifyContent="stretch"
                                        flexWrap="wrap"
                                        border="none"
                                        paddingInline={0}
                                    >
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                onServerValidate(values, setErrors, setStatus, setTouched, validateForm)
                                            }
                                            isDisabled={isDisabled}
                                            isLoading={isValidating}
                                        >
                                            <Box as="span" textTransform="capitalize">
                                                <FormattedMessage id="common_check_data" />
                                            </Box>
                                        </Button>

                                        <Box ml="auto">
                                            {wizardMode ? (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => {
                                                        setWizardMode(false);
                                                    }}
                                                    isDisabled={isSubmitting}
                                                >
                                                    <Box as="span" textTransform="capitalize">
                                                        <FormattedMessage id="common_skip" />
                                                    </Box>
                                                </Button>
                                            ) : (
                                                <Button variant="secondary" onClick={rest.onClose}>
                                                    <Box as="span" textTransform="capitalize">
                                                        <FormattedMessage id="common_cancel" />
                                                    </Box>
                                                </Button>
                                            )}

                                            <Button
                                                type="submit"
                                                isDisabled={isDisabled}
                                                isLoading={isSubmitting}
                                                ml={3}
                                            >
                                                <FormattedMessage id="common_item_create_btn" />
                                            </Button>
                                        </Box>
                                    </ModalFooter>
                                </Form>
                            );
                        }}
                    </Formik>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

const StyledRadio = styled(Radio)`
    margin-right: 2px;
    border-color: var(--chakra-colors-green-500);

    &:hover {
        border-color: var(--chakra-colors-blue-400);
    }
`;

export default AddSubBatteryModal;
